import { authClient } from '@aimfora/auth/client'
import { m } from '@aimfora/i18n'
import { focusFirstInvalid, Notice } from '@aimfora/ui'
import { useForm } from '@tanstack/react-form'
import { createFileRoute, Link } from '@tanstack/react-router'
import { type ReactNode, useRef, useState } from 'react'
import { z } from 'zod'
import { AuthCard, textLinkClass } from '~/components/auth-card'
import { FormError, SubmitButton, TextField } from '~/components/form-parts'
import { getPublicConfig } from '~/functions/session.functions'
import { authErrorMessage } from '~/lib/auth-errors'
import { emailOnlySchema, newPasswordSchema } from '~/lib/schemas'

export const Route = createFileRoute('/reset-password')({
  validateSearch: z.object({
    token: z.string().optional(),
    error: z.string().optional(),
  }),
  loader: () => getPublicConfig(),
  head: () => ({ meta: [{ title: `${m.reset_title()} – ${m.app_name()}` }] }),
  component: ResetPasswordPage,
})

/**
 * What to try when the mail does not come: the spam folder (with the address it comes from), and
 * that being approved for the beta is not the same as having an account, so there may be nothing
 * to reset. Said to everyone, so it does not tell whether an address has an account.
 */
function MailHelp({ sender }: { sender: string | null }) {
  return (
    <div className="space-y-2 text-ink-soft text-sm" data-testid="reset-help">
      <p>{sender ? m.reset_help_spam({ sender }) : m.reset_help_spam_nosender()}</p>
      <p>
        {m.reset_help_no_account()}{' '}
        <Link className={`font-semibold ${textLinkClass}`} to="/signup">
          {m.reset_help_signup_link()}
        </Link>
      </p>
    </div>
  )
}

function ResetPasswordPage() {
  const { token, error } = Route.useSearch()
  const { mailSender } = Route.useLoaderData()
  const back = (
    <Link className={textLinkClass} to="/login">
      {m.reset_back_to_login()}
    </Link>
  )

  if (token) {
    return (
      <AuthCard title={m.reset_new_title()} footer={back}>
        <NewPasswordForm token={token} />
      </AuthCard>
    )
  }
  return (
    <AuthCard title={m.reset_title()} footer={back}>
      {error && (
        <Notice tone="attention" role="alert" className="mb-4">
          {m.reset_invalid_token()}
        </Notice>
      )}
      <RequestLinkForm help={<MailHelp sender={mailSender} />} />
    </AuthCard>
  )
}

function RequestLinkForm({ help }: { help: ReactNode }) {
  const formRef = useRef<HTMLFormElement>(null)
  const [serverError, setServerError] = useState<string | null>(null)
  const [sentTo, setSentTo] = useState<string | null>(null)

  const form = useForm({
    defaultValues: { email: '' },
    validators: { onBlur: emailOnlySchema(), onSubmit: emailOnlySchema() },
    onSubmitInvalid: () => requestAnimationFrame(() => focusFirstInvalid(formRef.current)),
    onSubmit: async ({ value }) => {
      setServerError(null)
      const email = value.email.trim()
      const { error } = await authClient.requestPasswordReset({
        email,
        redirectTo: `${window.location.origin}/reset-password`,
      })
      if (error) {
        setServerError(authErrorMessage(error))
        return
      }
      // The same answer whether or not an account exists
      setSentTo(email)
    },
  })

  if (sentTo) {
    return (
      <div className="space-y-4">
        <p role="status" className="text-ink-soft text-sm">
          {m.reset_request_sent({ email: sentTo })}
        </p>
        {help}
      </div>
    )
  }

  return (
    <form
      ref={formRef}
      noValidate
      className="space-y-4"
      onSubmit={(event) => {
        event.preventDefault()
        void form.handleSubmit()
      }}
    >
      <p className="-mt-1 text-ink-soft text-sm">{m.reset_request_intro()}</p>
      <FormError message={serverError} />
      <form.Field name="email">
        {(field) => (
          <TextField
            field={field}
            label={m.auth_email_label()}
            type="email"
            autoComplete="email"
            autoFocus
          />
        )}
      </form.Field>
      <form.Subscribe selector={(state) => state.isSubmitting}>
        {(pending) => (
          <SubmitButton pending={pending} className="w-full">
            {m.reset_request_submit()}
          </SubmitButton>
        )}
      </form.Subscribe>
      {help}
    </form>
  )
}

function NewPasswordForm({ token }: { token: string }) {
  const formRef = useRef<HTMLFormElement>(null)
  const [serverError, setServerError] = useState<string | null>(null)
  const [done, setDone] = useState(false)

  const form = useForm({
    defaultValues: { password: '' },
    validators: { onBlur: newPasswordSchema(), onSubmit: newPasswordSchema() },
    onSubmitInvalid: () => requestAnimationFrame(() => focusFirstInvalid(formRef.current)),
    onSubmit: async ({ value }) => {
      setServerError(null)
      const { error } = await authClient.resetPassword({ newPassword: value.password, token })
      if (error) {
        setServerError(authErrorMessage(error))
        return
      }
      setDone(true)
    },
  })

  if (done) {
    return (
      <div className="space-y-4">
        <Notice tone="ok" role="status">
          {m.reset_done()}
        </Notice>
        <Link to="/login" className={`font-semibold ${textLinkClass}`}>
          {m.auth_login_title()}
        </Link>
      </div>
    )
  }

  return (
    <form
      ref={formRef}
      noValidate
      className="space-y-4"
      onSubmit={(event) => {
        event.preventDefault()
        void form.handleSubmit()
      }}
    >
      <FormError message={serverError} />
      <form.Field name="password">
        {(field) => (
          <TextField
            field={field}
            label={m.reset_new_label()}
            hint={m.auth_password_hint()}
            type="password"
            autoComplete="new-password"
            autoFocus
          />
        )}
      </form.Field>
      <form.Subscribe selector={(state) => state.isSubmitting}>
        {(pending) => (
          <SubmitButton pending={pending} className="w-full">
            {m.reset_new_submit()}
          </SubmitButton>
        )}
      </form.Subscribe>
    </form>
  )
}
